import ControlViewModel from '@/ViewModels/ControlViewModel';
import * as DefaultViewExtensionInterfaces from '#/DefaultViewExtensionInterfaces';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import MultiLinkViewModel from '@/ViewModels/Controls/MultiLinkViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ToolBarSpacerViewModelT from '@/ViewModels/Controls/ToolBarSpacerViewModel';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import LinkViewModelBuilder from '@/Builder/LinkViewModelBuilder';
import Utilities from '@/Utility/Utilities';
import {ToolBarItemViewModel} from '#/DefaultViewExtensionInterfaces';
import DropdownMenuViewModel from '@/Components/DropdownMenu/DropdownMenuViewModel';

type LinkCreationParameters = {
    position?: number;
    text?: ObservableOrDefault<string>;
    ergName?: ObservableOrDefault<string>;
    tooltipText?: ObservableOrDefault<string>;
    tooltipErgName?: ObservableOrDefault<string>;
    iconUrl?: ObservableOrDefault<string>;
    iconIsBinary?: boolean;
    iconSvg?: ObservableOrDefault<string>;
    isEnabled?: ObservableOrDefault<boolean>;
    onClick?: DefaultViewExtensionInterfaces.Handler;
    locator?: ObservableOrDefault<string>;
};
type MultiLinkCreationParameters = LinkCreationParameters & {
    items?: any[];
    initialMode?: boolean;
};
type ElementCreationParameters = MultiLinkCreationParameters | LinkCreationParameters;

export default class ToolBarViewModel extends ControlViewModel {
    /**
     * Gibt an, ob zu viele Elemente innerhalb der Secondary-Toolbar in einem [...]-Button gruppiert wurden 
     */
    public hasOverflowOnSecondaryItems: KnockoutObservable<boolean>;

    public detectOverflowOnSecondaryItems: KnockoutObservable<boolean>;

    /**
     * Die primären Links der ToolBar
     */
    public primary: KnockoutComputed<ToolBarItemViewModel[]>;

    /**
     * Die sekundären Links der ToolBar
     */
    public secondary: KnockoutComputed<ToolBarItemViewModel[]>;
    public showSecondary: KnockoutObservable<boolean>;

    /**
     * Die extended Links der ToolBar
     */
    public extended: KnockoutComputed<ToolBarItemViewModel[]>;
    public showExtended: KnockoutObservable<boolean>;

    private primaryItems: KnockoutObservableArray<ToolBarItemViewModel>;
    private secondaryItems: KnockoutObservableArray<ToolBarItemViewModel>;
    private extendedItems: KnockoutObservableArray<ToolBarItemViewModel>;

    constructor(params: DefaultViewExtensionInterfaces.IToolBarViewModelConstructorParameter) {
        super(params);
        this.hasOverflowOnSecondaryItems = this.observableOrDefault(false);
        this.detectOverflowOnSecondaryItems = this.observableOrDefault(params.detectOverflowOnSecondaryItems, true);
        this.primaryItems = this.observableOrDefault(params.primary, []) as KnockoutObservableArray<ToolBarItemViewModel>;
        this.secondaryItems = this.observableOrDefault(params.secondary, []) as KnockoutObservableArray<ToolBarItemViewModel>;
        this.extendedItems = this.observableOrDefault(params.extended, []) as KnockoutObservableArray<ToolBarItemViewModel>;
        this.primary = this.defineComputed({
            read: () => {
                return this.removeUnusedSpacer(this.primaryItems);
            },
            write: (value) => {
                this.primaryItems(value);
            }
        });
        this.secondary = this.defineComputed({
            read: () => {
                return this.removeUnusedSpacer(this.secondaryItems);
            },
            write: (value) => {
                this.secondaryItems(value);
            }
        });
        this.showSecondary = this.defineComputed(() => this.showList(this.secondary()));
        this.extended = this.defineComputed({
            read: () => {
                return this.removeUnusedSpacer(this.extendedItems);
            },
            write: (value) => {
                this.extendedItems(value);
            }
        });
        this.showExtended = this.defineComputed(() => this.showList(this.extended()));
    }

    private showList(items: ToolBarItemViewModel[]): boolean {
        return items.filter(i => i.isVisible && i.isVisible()).length > 0;
    }

    /**
     * Fügt ein neues primäres Element hinzu
     */
    public addPrimaryElement(params: ElementCreationParameters): void {
        this.addElement('primary', params);
    }

    /**
     * Fügt ein neues sekundäres Element hinzu
     */
    public addSecondaryElement(params: ElementCreationParameters): void {
        this.addElement('secondary', params);
    }

    /**
     * Fügt ein neues Element hinzu
     */
    public addElement(layer: 'primary' | 'secondary' | 'extended', params: ElementCreationParameters): void {
        const link: LinkViewModel = 'items' in params && !!params.items && 'initialMode' in params && !!params.initialMode
            ? this.createMultiLinkViewModel(params)
            : this.createLinkViewModel(params);
        const items = this[layer]();
        items.splice(params.position, 0, link);
        this[layer](items);
    }

    /**
     * Liefert alle primären Toolbar-Items vom Typ LinkViewModel
     */
    public get primaryLinks(): LinkViewModel[] {
        return this.getLinks(this.primary());
    }

    /**
     * Liefert alle sekundären Toolbar-Items vom Typ LinkViewModel
     */
    public get secondaryLinks(): LinkViewModel[] {
        return this.getLinks(this.secondary());
    }

    /**
     * Liefert alle erweiterten Toolbar-Items vom Typ LinkViewModel
     */
    public get extendedLinks(): LinkViewModel[] {
        return this.getLinks(this.extended());
    }

    private getLinks(arr: ToolBarItemViewModel[]): LinkViewModel[] {
        return arr.filter((e): e is LinkViewModel => this.isLink(e));
    }

    public isLink(item: ToolBarItemViewModel): item is LinkViewModel {
        return item instanceof LinkViewModel || item.type === 'LinkViewModel';
    }

    public isMultiLink(item: ToolBarItemViewModel): item is MultiLinkViewModel {
        return item instanceof MultiLinkViewModel || item.type === 'MultiLinkViewModel';
    }

    public isSpacer(item: ToolBarItemViewModel): item is ToolBarSpacerViewModelT {
        return item instanceof ToolBarSpacerViewModelT || item.type === 'ToolBarSpacerViewModel';
    }

    public isDropdownMenu(item: ToolBarItemViewModel): item is DropdownMenuViewModel {
        return item instanceof DropdownMenuViewModel || item.type === 'DropdownMenuViewModel';
    }

    /**
     * Liefert das Element der ToolBar anhand seines Textes
     */
    public getElementByCaption(caption: string): LinkViewModel {
        let link = null;
        const elements = this.getAllElements();

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            if (this.isLink(element) && element.label != null && element.label.caption() === caption) {
                link = elements[i];
            }
        }

        return link;
    }

    /**
     * Liefert das Element der ToolBar anhand seines ergonomischen Bezeichners
     */
    public getElementByErgName(ergName: string): LinkViewModel {
        let link = null;
        const elements = this.getAllElements();

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            if (this.isLink(element) && element.label != null && element.label.ergName() === ergName) {
                link = elements[i];
            }
        }

        return link;
    }

    /**
     * Liefert das Element der ToolBar anhand des aufrufenden Methodennamen
     */
    public getElementByMethodName(methodName: string): LinkViewModel {
        const elements = this.getAllElements();

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            if (this.isLink(element) && element.label != null) {
                const ergName = element.label.ergName();
                const uppercaseMethodName = methodName.toUpperCase();
                const toCheck = ergName.includes('_ERGNAME') ? `_${uppercaseMethodName}_ERGNAME` : uppercaseMethodName;

                if (ergName && ergName.includes(toCheck)) {
                    return elements[i] as LinkViewModel;
                }
            }
        }

        return null;
    }

    public getElementByTechnicalName(technicalName: string): LinkViewModel {
        const elements = this.getAllElements();

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            if (this.isLink(element) && element.technicalName == technicalName) {
                return element;
            }
        }

        return null;
    }

    /**
     * Entfernt ein Icon aus der Secondary-Toolbar
     */
    public removeSecondaryElementByErgName(ergName: string): any {
        let index = -1;
        const secondary = this.secondary();

        for (let i = 0; i < secondary.length; i++) {
            const item = secondary[i];
            if (this.isLink(item) && item.label != null && item.label.ergName() === ergName) {
                index = i;
            }
        }

        if (index > -1) {
            this.secondary().splice(index, 1);
            this.secondary.notifySubscribers(this.secondary());
        }
    }

    /**
     * Entfernt ein Icon aus der Secondary-Toolbar
     */
    public removeSecondaryElementByCaption(caption: string): void {
        let index = -1;
        const secondary = this.secondary();

        for (let i = 0; i < secondary.length; i++) {
            const item = secondary[i];
            if (this.isLink(item) && item.label != null && item.label.caption() === caption) {
                index = i;
            }
        }

        if (index > -1) {
            this.secondary().splice(index, 1);
            this.secondary.notifySubscribers(this.secondary());
        }
    }

    public getControlBindingForItem(item: any): {
        'uiGenerator': string;
        'viewModel': any;
    } {
        const ToolBarSpacerViewModel = ComponentManager.getViewModel<typeof ToolBarSpacerViewModelT>('ToolBarSpacerViewModel');

        if (this.isLink(item)) {
            return { uiGenerator: 'Link', viewModel: item.isToolBarLink(true) };
        }
        else if (this.isMultiLink(item)) {
            return { uiGenerator: 'MultiLink', viewModel: item };
        }
        else if (this.isSpacer(item)) {
            return { uiGenerator: 'ToolBarSpacer', viewModel: item };
        }
        else if (this.isDropdownMenu(item)) {
            return { uiGenerator: 'DropdownMenu', viewModel: item };
        }
        else if (!item.type) {
            return { uiGenerator: 'ToolBarSpacer', viewModel: new ToolBarSpacerViewModel({}) };
        }

        throw `Couldn't create control binding for item ${item}`;
    }

    public getClassForItem(item: any): string {
        if (!item.type) {
            return "c-separator";
        }
        else {
            return "";
        }
    }

    private createMultiLinkViewModel(params: MultiLinkCreationParameters): LinkViewModel {
        let image = null;
        let label = null;
        let tooltip = null;
        if (params.text || params.ergName) {
            label = new LabelViewModelBuilder()
                .withCaption(params.text)
                .withErgName(params.ergName)
                .build();
        }

        if (params.tooltipText != null || params.tooltipErgName != null) {
            tooltip = new LabelViewModelBuilder()
                .withCaption(params.tooltipText)
                .withErgName(params.tooltipErgName)
                .build();
        }

        if (params.iconUrl != null) {
            image = new ImageViewModelBuilder()
                .withSource(params.iconUrl)
                .withIsBinary(params.iconIsBinary)
                .withSvgSource(params.iconSvg)
                .updateProperties({
                    tooltip: tooltip
                }).build();
        }

        const link = new LinkViewModelBuilder()
            .updateProperties({
                image: image,
                label: label,
                tooltip: tooltip,
                isEnabled: params.isEnabled,
                onClick: params.onClick,
                parentLocator: this.locator,
                locator: params.locator
            }).build();

        return link;
    }

    private removeUnusedSpacer(items: KnockoutObservableArray<ToolBarItemViewModel>): Array<ToolBarItemViewModel> {
        const actualItems = Utilities.isFunction(items) ? items() : items;
        const newItems = [];
        const spacerSlots = [];

        if (Array.isArray(actualItems)) {
            for (let i = 0; i < actualItems.length; i++) {
                if (actualItems[i].type === 'ToolBarSpacerViewModel') {
                    spacerSlots.push(i);
                }
            }

            let lastSlot = 0;
            for (let i = 0; i < spacerSlots.length; i++) {
                const slice = actualItems.slice(lastSlot, spacerSlots[i]);
                lastSlot = spacerSlots[i] + 1;

                // Min. 1 Item sichtbar? -> Trenner muss eingefügt werden
                const visible = slice.some((item) => item.isVisible());
                slice.forEach(itemToInsert => newItems.push(itemToInsert))
                if (visible)
                    newItems.push({});
            }

            // Die restlichen Items nach dem letzten Trenner noch hinzufügen
            for (let i = lastSlot; i < actualItems.length; i++) {
                newItems.push(actualItems[i]);
            }

            return newItems;
        }

        return [];
    }

    private createLinkViewModel(params: LinkCreationParameters) {
        let image = null;
        let label = null;
        let tooltip = null;
        if (params.text || params.ergName) {
            label = new LabelViewModelBuilder()
                .updateProperties({
                    caption: params.text,
                    ergName: params.ergName,
                    parentLocator: this.defineComputed(() => {
                        if (Utilities.isFunction(params.locator)) {
                            return this.locator() + '.' + params.locator();
                        } else if (params.locator) {
                            return this.locator() + '.' + params.locator;
                        } else {
                            return null;
                        }
                    }),
                    locator: 'label'
                }).build();
        }

        if (params.tooltipText != null || params.tooltipErgName != null) {
            tooltip = new LabelViewModelBuilder()
                .withCaption(params.tooltipText)
                .withErgName(params.tooltipErgName)
                .build();
        }

        if (params.iconUrl != null) {
            image = new ImageViewModelBuilder()
                .updateProperties({
                    source: params.iconUrl,
                    isBinary: params.iconIsBinary,
                    svgSource: params.iconSvg,
                    alternativeText: tooltip
                }).build();
        }

        const link = new LinkViewModelBuilder()
            .updateProperties({
                image: image,
                label: label,
                tooltip: tooltip,
                isEnabled: params.isEnabled,
                onClick: params.onClick,
                parentLocator: this.locator,
                locator: params.locator
            }).build();

        return link;
    }

    /**
     * Hilfsfunktion zum Ermitteln aller Elemente
     */
    private getAllElements(): ToolBarItemViewModel[] {
        return this.primary().concat(this.secondary()).concat(this.extended());
    }
}

ComponentManager.setViewModel('ToolBarViewModel', ToolBarViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');